import {
  Badge,
  BlockStack,
  Banner,
  Button,
  Card,
  Checkbox,
  Collapsible,
  InlineStack,
  Layout,
  Modal,
  Page,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  createSeasonalProposals,
  decideSeasonalRules,
  listSeasonalRules,
  type SeasonalRuleEvent,
} from "../api/seasonal_rules";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { currentLocale, t } from "../lib/i18n";

const QUERY_KEY = ["seasonal-rules"];

function selectionRevision(event: SeasonalRuleEvent): string {
  return [
    ...event.proposed.map((product) => `proposed:${product.product_id}`),
    ...event.decided.map(
      (product) =>
        `decided:${product.product_id}:${product.applies ? "1" : "0"}`,
    ),
  ]
    .sort()
    .join("|");
}

function appliedProductCount(event: SeasonalRuleEvent): number {
  return event.decided.filter((product) => product.applies).length;
}

function needsMerchantReview(event: SeasonalRuleEvent): boolean {
  return (
    event.decided.length + event.proposed.length > 0 &&
    appliedProductCount(event) === 0 &&
    !event.is_reviewed
  );
}

function formatCountry(countryCode: string): string {
  const code = countryCode.trim().toUpperCase();
  if (!code) return countryCode;

  try {
    const locale = currentLocale();
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
    if (!name || name === code) return code;
    return locale === "ja" ? `${name}（${code}）` : `${name} (${code})`;
  } catch {
    // 未対応ブラウザや未知の国コードでも、API が返したコード自体は失わない。
    return code;
  }
}

/**
 * 商品別の季節係数（母の日にトイレットペーパーを ×1.2 しない）。
 *
 * 🔴 **承認するまで係数は1つも効かない。** 未判断・AI提案・旧国の判断を
 * 店全体適用へ倒さない。マーチャントがチェックして確定した商品だけが対象。
 *
 * 🔴 **AI は提案するだけ。** 決めるのはマーチャントで、承認して初めて発注数量に効く。
 */
export function SeasonalRules() {
  const queryClient = useQueryClient();
  const rulesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listSeasonalRules,
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const proposeMutation = useMutation({
    mutationFn: createSeasonalProposals,
    onSuccess: invalidate,
  });

  return (
    <Page
      title={t("季節性の商品別設定")}
      subtitle={t(
        "どの商品がどの季節イベントで伸びるかを決めます。確定するまで季節係数は適用されません。",
      )}
      primaryAction={{
        content: t("提案を作り直す"),
        onAction: () => proposeMutation.mutate(),
        loading: proposeMutation.isPending,
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {proposeMutation.isError && (
              // 🔴 縮退しない＝失敗を正直に出す。承認するまで係数は変わらないので、
              //    提案が作れなくてもアプリは従来どおり動く（実害ゼロ）。
              <Banner tone="warning" title={t("提案を作成できませんでした")}>
                <p>
                  {t(
                    "時間をおいて再度お試しください。提案を作れない間も、未確認の商品に季節係数は適用されません。",
                  )}
                </p>
              </Banner>
            )}
            {proposeMutation.isSuccess && (
              <Banner tone="success" title={t("提案ができました")}>
                <p>
                  {t(
                    "新しい候補も自動では適用されません。「確認が必要」のイベントを確認してください。",
                  )}
                </p>
              </Banner>
            )}
            {rulesQuery.isLoading ? (
              <Card>
                <SkeletonBodyText lines={8} />
              </Card>
            ) : rulesQuery.data ? (
              rulesQuery.data.events.length === 0 ? (
                <Card>
                  <Text as="p" tone="subdued">
                    {t(
                      "このストアの所在国には固定カレンダーの季節補正がありません。設定する項目もありません。",
                    )}
                  </Text>
                </Card>
              ) : (
                <SeasonalRuleEvents
                  countryCode={rulesQuery.data.country_code}
                  events={rulesQuery.data.events}
                  onDecided={invalidate}
                />
              )
            ) : (
              <QueryErrorBanner
                title={t("季節性の設定を取得できませんでした")}
                onRetry={() => void rulesQuery.refetch()}
              />
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function SeasonalRuleEvents({
  countryCode,
  events,
  onDecided,
}: {
  countryCode: string | null;
  events: SeasonalRuleEvent[];
  onDecided: () => void;
}) {
  const [showOtherEvents, setShowOtherEvents] = useState(false);
  const reviewEvents = events.filter(needsMerchantReview);
  const otherEvents = events.filter((event) => !needsMerchantReview(event));

  return (
    <BlockStack gap="400">
      <Banner tone="info" title={t("AI候補は自動適用されません")}>
        <BlockStack gap="100">
          {countryCode && (
            <Text as="p">
              {t("ストアの国: {country}", {
                country: formatCountry(countryCode),
              })}
            </Text>
          )}
          <Text as="p">
            {t(
              "「確認が必要」のイベントで、実際に売上が伸びる商品を1件以上選んでください。商品を選ぶと適用操作が表示されます。",
            )}
          </Text>
          <Text as="p">
            {t(
              "適用した商品だけが、表示された期間中の発注数量で季節係数の対象になります。",
            )}
          </Text>
        </BlockStack>
      </Banner>

      {reviewEvents.length > 0 ? (
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            {t("確認が必要")}
          </Text>
          {reviewEvents.map((event) => (
            <EventCard
              key={`${event.slug}:${selectionRevision(event)}`}
              event={event}
              onDecided={onDecided}
            />
          ))}
        </BlockStack>
      ) : (
        <Text as="p" tone="subdued">
          {t("確認が必要なイベントはありません。")}
        </Text>
      )}

      {otherEvents.length > 0 && (
        <BlockStack gap="200">
          <Button
            variant="plain"
            onClick={() => setShowOtherEvents((open) => !open)}
            ariaExpanded={showOtherEvents}
            ariaControls="seasonal-other-events"
          >
            {showOtherEvents
              ? t("適用済み・季節補正なし・対象商品なしを閉じる")
              : t("適用済み・季節補正なし・対象商品なし（{n}件）", {
                  n: otherEvents.length,
                })}
          </Button>
          <Collapsible
            open={showOtherEvents}
            id="seasonal-other-events"
            transition={{ duration: "150ms", timingFunction: "ease" }}
          >
            <BlockStack gap="300">
              {otherEvents.map((event) => (
                <EventCard
                  key={`${event.slug}:${selectionRevision(event)}`}
                  event={event}
                  onDecided={onDecided}
                />
              ))}
            </BlockStack>
          </Collapsible>
        </BlockStack>
      )}
    </BlockStack>
  );
}

function EventCard({
  event,
  onDecided,
}: {
  event: SeasonalRuleEvent;
  onDecided: () => void;
}) {
  // AI候補は安全側の未選択。人が判断済みの行だけ、保存された現状を初期値にする。
  // 候補をチェック済みに見せると、内容を読まず主ボタンを押しただけで誤適用できる。
  const [checked, setChecked] = useState<Record<string | number, boolean>>(() => {
    const initial: Record<string | number, boolean> = {};
    for (const p of event.proposed) initial[p.product_id] = false;
    for (const p of event.decided) initial[p.product_id] = p.applies;
    return initial;
  });
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const decideMutation = useMutation({
    mutationFn: () =>
      decideSeasonalRules(
        event.slug,
        rows.map((p) => ({
          product_id: p.product_id,
          applies: checked[p.product_id] ?? false,
        })),
      ),
    // commit 後の応答喪失では、失敗に見えても保存済みの可能性がある。
    // 成否にかかわらず再取得し、画面をサーバーの状態へ収束させる。
    onSettled: () => {
      setShowRemoveConfirmation(false);
      onDecided();
    },
  });

  // 同じ商品が提案と判断済みの両方に出ることはない（API が振り分ける）。
  const rows = [...event.decided, ...event.proposed];
  const appliedCount = appliedProductCount(event);
  const selectedCount = rows.filter(
    (product) => checked[product.product_id] ?? false,
  ).length;
  const decidedProductIds = new Set(
    event.decided.map((product) => product.product_id),
  );
  const hasSelectionChanged = rows.some((product) => {
    const savedValue = decidedProductIds.has(product.product_id)
      ? product.applies
      : false;
    return (checked[product.product_id] ?? false) !== savedValue;
  });
  // チェック済みのうち、発注点が手動固定＝適用しても数量が動かない商品。
  const blockedChecked = rows.filter(
    (p) => p.reorder_point_is_manual && (checked[p.product_id] ?? false),
  );

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {event.name}
            </Text>
            <Badge>{`×${event.multiplier}`}</Badge>
            <Text as="span" variant="bodySm" tone="subdued">
              {t("適用期間: {start}〜{end}", {
                start: event.window_start,
                end: event.window_end,
              })}
            </Text>
          </InlineStack>
          {/* 🔴 係数は期間の中でしか掛からない＝「いま適用中」と言い切ると
              8月に年末年始が効いているように読める嘘になる（実際に指摘された）。
              時期（期間中か）と判断状態（商品を絞ったか）を分けて言う。 */}
          {rows.length === 0 ? (
            <Badge>{t("対象商品なし")}</Badge>
          ) : appliedCount === 0 && !event.is_reviewed ? (
            <Badge tone="attention">
              {rows.length === 1
                ? t("確認が必要（1候補）")
                : t("確認が必要（{n}候補）", { n: rows.length })}
            </Badge>
          ) : appliedCount === 0 ? (
            <Badge>{t("季節補正なし")}</Badge>
          ) : (
            <Badge tone="success">
              {appliedCount === 1
                ? t("適用済み（1商品）")
                : t("適用済み（{n}商品）", { n: appliedCount })}
            </Badge>
          )}
        </InlineStack>

        {rows.length > 0 && (
          <>
            <BlockStack gap="100">
              {rows.map((p) => (
                <BlockStack key={p.product_id} gap="050">
                  <Checkbox
                    label={
                      p.on_placed_purchase_order ? (
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span">{p.title ?? `#${p.product_id}`}</Text>
                          {/* 季節係数の適用状態ではなく、既存の発行済み注文書の状態。
                            「適用済み」は非季節商品に係数が掛かったように読めるので使わない。 */}
                          <span aria-hidden="true">
                            <Badge tone="info">{t("発行済み注文書あり")}</Badge>
                          </span>
                        </InlineStack>
                      ) : (
                        (p.title ?? `#${p.product_id}`)
                      )
                    }
                    checked={checked[p.product_id] ?? false}
                    onChange={(value) =>
                      setChecked((prev) => ({ ...prev, [p.product_id]: value }))
                    }
                    helpText={
                      p.reorder_point_is_manual
                        ? t(
                            "発注点を手動で固定しているため、係数は発注数量に影響しません",
                          )
                        : p.on_placed_purchase_order
                          ? t(
                              "すでに発行済みの注文書に入っています。いま適用しても、その注文書の数量は変わりません（次に作る注文書から効きます）。",
                            )
                          : undefined
                    }
                  />
                </BlockStack>
              ))}
            </BlockStack>

            {/* 🔴 「適用したのに数量が動かない」を押す前に言う。手動固定の発注点は
                需要を見ないので、需要に掛かる係数は届かない（実機で踏んだ）。 */}
            {blockedChecked.length > 0 && (
              <Banner
                tone="warning"
                title={t("選んだ商品のうち、数量が変わらないものがあります")}
              >
                <Text as="p">
                  {t(
                    "{names} は発注点を手動で固定しているため、係数を適用しても発注数量は変わりません。",
                    {
                      names: blockedChecked
                        .map((p) => p.title ?? `#${p.product_id}`)
                        .join("、"),
                    },
                  )}
                </Text>
                <Text as="p">
                  {t(
                    "「発注点設定」でこれらの商品の発注点を空にすると、販売速度から自動計算されるようになり、季節係数が数量に反映されます。",
                  )}
                </Text>
              </Banner>
            )}
            {decideMutation.isError && (
              <Banner
                tone="critical"
                title={t("設定の適用結果を確認できませんでした")}
              >
                <Text as="p">
                  {t(
                    "通信状況によっては適用済みの可能性があります。最新の状態を再取得しています。チェック状態を確認してから、必要な場合だけもう一度お試しください。",
                  )}
                </Text>
              </Banner>
            )}
            {hasSelectionChanged && selectedCount === 0 && appliedCount > 0 && (
              <BlockStack gap="200">
                <Text as="p" tone="critical" variant="bodySm">
                  {t(
                    "すべてのチェックを外しています。保存済みの季節補正を解除する場合は、明示的に確定してください。",
                  )}
                </Text>
                <InlineStack align="end">
                  <Button
                    tone="critical"
                    onClick={() => setShowRemoveConfirmation(true)}
                  >
                    {t("このイベントの適用を解除")}
                  </Button>
                </InlineStack>
              </BlockStack>
            )}
            {hasSelectionChanged && selectedCount > 0 && (
              <InlineStack align="end">
                <Button
                  variant="primary"
                  loading={decideMutation.isPending}
                  onClick={() => decideMutation.mutate()}
                >
                  {appliedCount > 0
                    ? t("変更を保存")
                    : selectedCount === 1
                      ? t("選んだ1商品に適用")
                      : t("選んだ{n}商品に適用", { n: selectedCount })}
                </Button>
              </InlineStack>
            )}
          </>
        )}
      </BlockStack>
      <Modal
        open={showRemoveConfirmation}
        onClose={() => {
          if (!decideMutation.isPending) setShowRemoveConfirmation(false);
        }}
        title={t("このイベントの季節補正を解除しますか？")}
        primaryAction={{
          content: t("季節補正を解除する"),
          destructive: true,
          loading: decideMutation.isPending,
          disabled: decideMutation.isPending,
          onAction: () => decideMutation.mutate(),
        }}
        secondaryActions={[
          {
            content: t("解除しない"),
            disabled: decideMutation.isPending,
            onAction: () => setShowRemoveConfirmation(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            {t(
              "{name}で現在適用中の{n}商品から季節係数を外します。候補商品は削除されません。",
              { name: event.name, n: appliedCount },
            )}
          </Text>
        </Modal.Section>
      </Modal>
    </Card>
  );
}
